// C3 — first-class manual "Flag a problem".
//
// Any employee can flag a problem on any record from the in-record
// Cleanup drawer (including records with ZERO detected issues). The
// flag becomes a real CleanupIssue (source_kind='manual', state
// 'new') so it flows through the SAME research → decide → undo →
// audit engine as detector-emitted issues — no separate queue.
//
// This module is the PURE, root-safe core: the small Stewardship-
// specific problem-type vocabulary (UX rule #9: plain words, not
// jargon) and the templating that turns a chosen type into the
// issue's decision question + plain-language flag explanation. Kept
// out of the route / UI so it is unit-tested in isolation and shared
// verbatim by the worker (root tsconfig) and the web UI (web
// tsconfig) — exactly how cleanup-survivorship-matrix is shared.

export type ManualProblemType =
  | "duplicate"
  | "wrong_info"
  | "wrong_household"
  | "wrong_advisor"
  | "should_archive"
  | "not_sure"
  | "other";

export interface ManualProblemTypeDef {
  value: ManualProblemType;
  // What the flagger sees in the picker. Plain words (UX rule #9).
  label: string;
  // One-line hint under the option ("when to use this").
  hint: string;
}

// The picker, in the order it should render. "Not sure — needs a
// look" is deliberately included (UX/service-blueprint: a flagger who
// can't classify it must still have a no-friction path) and sits just
// above "Something else".
export const MANUAL_PROBLEM_TYPES: readonly ManualProblemTypeDef[] = [
  {
    value: "duplicate",
    label: "Looks like a duplicate",
    hint: "This record seems to be the same person/entity as another.",
  },
  {
    value: "wrong_info",
    label: "Information is wrong or outdated",
    hint: "A name, email, phone, address, or other field is incorrect.",
  },
  {
    value: "wrong_household",
    label: "In the wrong household",
    hint: "This contact is grouped with the wrong family/household.",
  },
  {
    value: "wrong_advisor",
    label: "Wrong advisor / owner",
    hint: "The assigned advisor or owner on this record looks wrong.",
  },
  {
    value: "should_archive",
    label: "Should be archived",
    hint: "Inactive, not a real record, or no longer relevant.",
  },
  {
    value: "not_sure",
    label: "Not sure — needs a look",
    hint: "Something seems off but you can't classify it. That's OK.",
  },
  {
    value: "other",
    label: "Something else",
    hint: "Anything not covered above — explain in the note.",
  },
] as const;

const VALID = new Set<string>(MANUAL_PROBLEM_TYPES.map((t) => t.value));

export function isManualProblemType(v: unknown): v is ManualProblemType {
  return typeof v === "string" && VALID.has(v);
}

// The CleanupIssue family a manual flag enters under. "needs-
// attention" is the catch-all triage family the workspace already
// renders a lane for.
export const MANUAL_ISSUE_FAMILY = "needs-attention" as const;

// Per-type decision question + plain-language flag explanation. These
// strings frame WHAT kind of problem was reported, in plain words, so a
// non-expert opening the issue understands it in 3 seconds (UX rule #1).
//
// The flagger's required note carries the specifics and now renders
// inline directly under this explanation (ResearchPane note thread), so
// the copy no longer dangles a "see their note" pointer at a note the UI
// used to hide. It also names the real flagger when known — passing
// `flaggerName` replaces the old hardcoded "A team member", which left
// every human flag anonymous even though the identity was stored.
export function buildManualIssueCopy(
  problemType: ManualProblemType,
  recordLabel: string,
  flaggerName?: string | null,
): { decisionQuestion: string; flagExplanation: string } {
  const who = flaggerName?.trim() || "A team member";
  const safeLabel = recordLabel.trim() || "this record";
  switch (problemType) {
    case "duplicate":
      return {
        decisionQuestion: `Is ${safeLabel} a duplicate that should be merged?`,
        flagExplanation: `${who} flagged ${safeLabel} as a possible duplicate of another record — research the match and merge or dismiss.`,
      };
    case "wrong_info":
      return {
        decisionQuestion: `What information on ${safeLabel} needs correcting?`,
        flagExplanation: `${who} reported that information on ${safeLabel} is wrong or outdated — fix the field(s) and resolve.`,
      };
    case "wrong_household":
      return {
        decisionQuestion: `Which household should ${safeLabel} be in?`,
        flagExplanation: `${who} reported that ${safeLabel} is in the wrong household — move the contact or dismiss.`,
      };
    case "wrong_advisor":
      return {
        decisionQuestion: `Who should own ${safeLabel}?`,
        flagExplanation: `${who} reported that the advisor / owner on ${safeLabel} looks wrong — reassign or dismiss.`,
      };
    case "should_archive":
      return {
        decisionQuestion: `Should ${safeLabel} be archived?`,
        flagExplanation: `${who} reported that ${safeLabel} should be archived (inactive, not a real record, or no longer relevant) — confirm and archive or dismiss.`,
      };
    case "not_sure":
      return {
        decisionQuestion: `${safeLabel} was flagged for review — what's going on?`,
        flagExplanation: `${who} noticed something off about ${safeLabel} but couldn't classify it — research and decide.`,
      };
    case "other":
    default:
      return {
        decisionQuestion: `${safeLabel} was flagged — what needs to happen?`,
        flagExplanation: `${who} flagged ${safeLabel} for a reason that didn't fit the standard categories.`,
      };
  }
}
